import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  StarIcon,
  ShoppingCartIcon,
  HeartIcon,
  ShareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TruckIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  PhoneIcon,
  CheckIcon,
  MinusIcon,
  PlusIcon,
  SparklesIcon,
  CameraIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { dbOperations, priceHelpers } from '../utils/supabase';
import { useCart } from '../context/CartContext';
import AIVisualizationModal from '../components/AIVisualizationModal';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart: addToCartContext } = useCart();
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  
  // Yeni takım mantığı için state'ler
  const [setItemQuantities, setSetItemQuantities] = useState({});
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [settings, setSettings] = useState({ profit_margin: 70, set_discount_rate: 5 });
  
  // Takım fiyat hesaplamaları için state'ler
  const [setTotal, setSetTotal] = useState(0);
  const [setDiscount, setSetDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  
  // Toast notification state'leri
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Settings'i yükle
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const appSettings = await priceHelpers.getSettings();
        setSettings(appSettings);
      } catch (error) {
        console.error('Settings yükleme hatası:', error);
      }
    };

    loadSettings();
  }, []);

  // Ürün verilerini çek
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const { data, error } = await dbOperations.products.getById(id);
        
        if (error) {
          console.error('Ürün yüklenirken hata:', error);
          return;
        }

        // Fiyatları formatla (kar oranı dahil)
        const formattedProduct = await priceHelpers.formatProductPrice({
          ...data,
          rating: data.rating || (4.5 + Math.random() * 0.5), // Rastgele rating ekle
          reviews: data.reviews || Math.floor(Math.random() * 200) + 50 // Rastgele review sayısı
        });
        setProduct(formattedProduct);

        // Eğer takım ürünü ise, takım elemanlarının miktarlarını initialize et ve satış fiyatlarını hesapla
        if (formattedProduct.is_set && formattedProduct.set_items) {
          const initialQuantities = {};
          
          // Takım elemanlarının satış fiyatlarını hesapla
          const itemsWithSalePrice = await Promise.all(
            formattedProduct.set_items.map(async (item) => {
              const salePrice = await priceHelpers.calculateSalePrice(item.price);
              initialQuantities[item.id] = item.quantity;
              return {
                ...item,
                salePrice
              };
            })
          );
          
          setSetItemQuantities(initialQuantities);
          // Ürünü güncellenmiş takım elemanları ile güncelle
          setProduct({
            ...formattedProduct,
            set_items: itemsWithSalePrice
          });
        }
      } catch (error) {
        console.error('Ürün yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Benzer ürünleri getir
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product || !product.category) return;
      
      try {
        const { data, error } = await dbOperations.products.getAll();
        if (!error && data) {
          const related = data
            .filter(p => p.id !== product.id && p.category === product.category && p.is_active)
            .slice(0, 3);
          
          // Related products'u da fiyat formatı ile güncelle
          const relatedWithRating = related.map(product => ({
            ...product,
            rating: product.rating || (4.5 + Math.random() * 0.5), // Rastgele rating ekle
            reviews: product.reviews || Math.floor(Math.random() * 200) + 50 // Rastgele review sayısı
          }));
          const formattedRelated = await priceHelpers.formatProductList(relatedWithRating);
          setRelatedProducts(formattedRelated);
        }
      } catch (error) {
        console.error('Benzer ürünler yüklenirken hata:', error);
      }
    };

    fetchRelatedProducts();
  }, [product]);

  // Takım fiyatlarını hesapla ve state'leri güncelle
  useEffect(() => {
    const updateSetPrices = async () => {
      if (product && product.is_set && product.set_items) {
        try {
          const total = await calculateSetTotal();
          const discount = await calculateSetDiscount();
          const final = total - discount;
          
          setSetTotal(total);
          setSetDiscount(discount);
          setFinalPrice(final);
        } catch (error) {
          console.error('Takım fiyat hesaplama hatası:', error);
        }
      }
    };

    updateSetPrices();
  }, [product, setItemQuantities, settings]);

  // Takım elemanı miktarını güncelle
  const updateSetItemQuantity = (itemId, newQuantity) => {
    if (newQuantity < 0) return;
    setSetItemQuantities(prev => ({
      ...prev,
      [itemId]: newQuantity
    }));
  };

  // Takım toplam fiyatını hesapla
  const calculateSetTotal = async () => {
    if (!product || !product.is_set || !product.set_items) {
      return product?.price || 0;
    }

    // Takım ürünleri için takım elemanlarının kar oranı dahil toplam fiyatı
    let setItemsTotal = 0;
    for (const item of product.set_items) {
      const quantity = setItemQuantities[item.id] || 0;
      // item.salePrice zaten hesaplanmış satış fiyatı
      const salePrice = item.salePrice || await priceHelpers.calculateSalePrice(item.price);
      setItemsTotal += quantity * salePrice;
    }

    return setItemsTotal;
  };

  // Takım indirimi hesapla (settings'ten oranı al)
  const calculateSetDiscount = async () => {
    if (!product || !product.is_set) return 0;
    
    // Her item için minimum şartı kontrol et (item ID bazında)
    let meetsAllRequirements = true;
    
    if (product.set_items) {
      for (const item of product.set_items) {
        const currentQuantity = setItemQuantities[item.id] !== undefined 
          ? setItemQuantities[item.id] 
          : item.quantity;
        
        // Her item için minimum adet = default quantity
        const minimumRequired = item.quantity;
        
        if (currentQuantity < minimumRequired) {
          meetsAllRequirements = false;
          break;
        }
      }
    }
    
    // Eğer TÜM minimum gereksinimleri karşılamıyorsa indirim yok
    if (!meetsAllRequirements) {
      return 0;
    }
    
    // TÜM minimum gereksinimleri karşılıyorsa settings'teki oranı uygula
    const total = await calculateSetTotal();
    return Math.round(total * (settings.set_discount_rate / 100) * 100) / 100;
  };

  // Final fiyat hesapla
  const calculateFinalPrice = async () => {
    if (!product || !product.is_set) {
      return product?.price || 0;
    }
    const total = await calculateSetTotal();
    const discount = await calculateSetDiscount();
    return total - discount;
  };

  useEffect(() => {
    console.log('ProductDetailPage useEffect - ID:', id, 'Product found:', !!product);
    
    if (product) {
      if (product.sizes && product.sizes.length > 0) {
        setSelectedSize(product.sizes[0]);
      }
      if (product.colors && product.colors.length > 0) {
        setSelectedColor(product.colors[0]);
      }
    } else if (id) {
      // Ürün bulunamazsa 2 saniye bekle sonra yönlendir
      const timer = setTimeout(() => {
        console.log('Ürün bulunamadı, products sayfasına yönlendiriliyor...');
        navigate('/products');
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [id, product, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Ürün bulunamadı</h2>
          <p className="text-gray-600 mt-2">Aradığınız ürün mevcut değil.</p>
          <Link to="/products" className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Ürünlere Dön
          </Link>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    const imagesLength = product.images ? product.images.length : (product.image_url ? 1 : 0);
    setCurrentImageIndex((prev) => (prev + 1) % imagesLength);
  };

  const prevImage = () => {
    const imagesLength = product.images ? product.images.length : (product.image_url ? 1 : 0);
    setCurrentImageIndex((prev) => (prev - 1 + imagesLength) % imagesLength);
  };

  const addToCart = async () => {
    if (product.is_set) {
      // Takım ürünü için
      const activeSetItems = product.set_items.filter(item => {
        const currentQuantity = setItemQuantities[item.id] !== undefined 
          ? setItemQuantities[item.id] 
          : item.quantity;
        return currentQuantity > 0;
      });

      const cartItem = {
        id: product.id,
        name: product.name,
        price: finalPrice,
        originalPrice: setTotal,
        discount: setDiscount,
        image: product.image_url || (product.images && product.images[0]?.url),
        isSet: true,
        setItems: activeSetItems.map(item => ({
          ...item,
          quantity: setItemQuantities[item.id] !== undefined ? setItemQuantities[item.id] : item.quantity
        })),
        category: product.category
      };

      // Gerçek sepete ekle
      addToCartContext(cartItem, 1);

      // Toast notification göster
      setToastData({
        type: 'set',
        productName: product.name,
        finalPrice: finalPrice,
        activeSetItems: activeSetItems.map(item => ({
          name: item.name,
          quantity: setItemQuantities[item.id] !== undefined ? setItemQuantities[item.id] : item.quantity
        })),
        image: cartItem.image
      });
      setShowToast(true);

      // Toast'u 4 saniye sonra gizle
      setTimeout(() => setShowToast(false), 4000);
    } else {
      // Normal ürün için
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.salePrice || product.price,
        image: product.image_url || (product.images && product.images[0]?.url),
        isSet: false,
        category: product.category
      };

      // Gerçek sepete ekle
      addToCartContext(cartItem, quantity, {
        size: selectedSize,
        color: selectedColor
      });

      // Toast notification göster
      setToastData({
        type: 'single',
        productName: product.name,
        quantity: quantity,
        price: product.salePrice || product.price,
        image: cartItem.image,
        options: {
          size: selectedSize,
          color: selectedColor
        }
      });
      setShowToast(true);

      // Toast'u 4 saniye sonra gizle
      setTimeout(() => setShowToast(false), 4000);
    }
  };

  const handleAIVisualization = () => {
    // AI modal'ını aç - kullanıcı kendi görselin yükleyecek
    setShowAIModal(true);
  };

  // AI görseli mevcut mu kontrol et (yeni çoklu sistem)
  const hasAIImages = () => {
    // ai_images array'i varsa ve dolu ise
    if (product?.ai_images && Array.isArray(product.ai_images) && product.ai_images.length > 0) {
      return true;
    }
    // Geriye uyumluluk için ai_image_url kontrol et
    if (product?.ai_image_url && product.ai_image_url.trim() !== '') {
      return true;
    }
    return false;
  };

  const discountPercentage = product.salePrice 
    ? Math.round((1 - product.salePrice / product.price) * 100) 
    : 0;

  const tabs = [
    { id: 'description', name: 'Açıklama' },
    { id: 'specifications', name: 'Özellikler' },
    { id: 'ai-visualization', name: 'AI Görselleştirme' },
    { id: 'reviews', name: `Değerlendirmeler (${product?.reviews || 0})` }
  ];

  return (
    <>
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-slate-900 transition-colors">
            Ana Sayfa
          </Link>
          <span>/</span>
          <Link to="/products" className="hover:text-slate-900 transition-colors">
            Ürünler
          </Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden bg-white shadow-lg cursor-pointer group" onClick={() => setShowImageModal(true)}>
              <img
                src={product.images && product.images.length > 0 
                  ? product.images[currentImageIndex]?.url || product.images[currentImageIndex]
                  : product.image_url || 'https://via.placeholder.com/600x400?text=Resim+Yok'
                }
                alt={product.name}
                className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-300"
              />
              
              {/* Click to zoom indicator */}
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <CameraIcon className="h-4 w-4 inline mr-1" />
                Büyütmek için tıklayın
              </div>
              
              {/* Image Navigation */}
              {((product.images && product.images.length > 1) || (product.image_url && currentImageIndex > 0)) && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all"
                  >
                    <ChevronLeftIcon className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all"
                  >
                    <ChevronRightIcon className="h-6 w-6" />
                  </button>
                </>
              )}

              {/* Sale Badge */}
              {product.salePrice && (
                <div className="absolute top-4 right-4 bg-red-500 px-3 py-2 rounded-lg shadow-sm">
                  <span className="text-sm font-bold text-white">
                    %{Math.round((1 - product.salePrice / product.price) * 100)} İndirim
                  </span>
                </div>
              )}

              {/* Takım Badge */}
              {product.is_set && (
                <div className="absolute top-4 left-4 bg-blue-500 px-3 py-2 rounded-lg shadow-sm">
                  <span className="text-sm font-bold text-white">
                    🛋️ Takım Ürünü
                  </span>
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {product.images && product.images.length > 1 && (
              <div className="flex space-x-3 overflow-x-auto">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      currentImageIndex === index 
                        ? 'border-slate-900' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={image?.url || image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="inline-block bg-slate-100 text-slate-800 text-sm font-medium px-3 py-1 rounded-full">
                  {product.category}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsFavorite(!isFavorite)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    {isFavorite ? (
                      <HeartSolidIcon className="h-6 w-6 text-red-500" />
                    ) : (
                      <HeartIcon className="h-6 w-6 text-gray-400" />
                    )}
                  </button>
                  <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                    <ShareIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon
                      key={i}
                      className={`h-5 w-5 ${
                        i < Math.floor(product.rating || 0)
                          ? 'text-amber-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {product.rating?.toFixed(1) || '4.5'}
                </span>
                <span className="text-sm text-gray-500">
                  ({product.reviews || 0} değerlendirme)
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="border-t border-b border-gray-200 py-6">
              {product.is_set ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
                      🛋️ Takım Fiyatı
                    </h3>
                    <div className="space-y-2">
                      {product.set_items && product.set_items.map(item => {
                        const quantity = setItemQuantities[item.id] || 0;
                        if (quantity === 0) return null;
                        // item.price alım fiyatı, kar oranını ekleyerek müşteriye gösterilecek fiyatı hesapla
                        const displayPrice = item.price * (1 + settings.profit_margin / 100);
                        return (
                          <div key={item.id} className="flex justify-between text-sm text-blue-700">
                            <span>{item.name} ({quantity} adet)</span>
                            <span>₺{(quantity * displayPrice).toLocaleString()}</span>
                          </div>
                        );
                      })}
                      <div className="border-t border-blue-200 pt-2">
                        <div className="flex justify-between text-sm text-blue-800">
                          <span className="font-medium">Ara Toplam</span>
                          <span className="font-medium">₺{setTotal.toLocaleString()}</span>
                        </div>
                        
                        {/* Takım indirimi kontrol ve bilgi */}
                        {setDiscount > 0 ? (
                          <div className="flex justify-between text-sm text-green-700">
                            <span>🎁 Takım İndirimi (%{settings.set_discount_rate})</span>
                            <span>-₺{setDiscount.toLocaleString()}</span>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm text-gray-500">
                              <span>🚫 Takım İndirimi (Uygulanmıyor)</span>
                              <span>₺0</span>
                            </div>
                            <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border">
                              <strong>ℹ️ Takım indirimi için minimum:</strong>
                              {product.set_items.map(item => (
                                <div key={item.id}>• {item.name}: En az {item.quantity} adet</div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-between text-lg font-bold text-blue-900 border-t border-blue-300 pt-2 mt-2">
                          <span>🎯 Final Takım Fiyatı</span>
                          <span>₺{finalPrice.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  {product.salePrice ? (
                    <>
                      <span className="text-3xl font-bold text-emerald-600">
                        ₺{product.salePrice.toLocaleString()}
                      </span>
                      <span className="text-xl text-gray-400 line-through">
                        ₺{product.price.toLocaleString()}
                      </span>
                      <span className="bg-emerald-100 text-emerald-800 text-sm font-medium px-2 py-1 rounded">
                        ₺{(product.price - product.salePrice).toLocaleString()} tasarruf
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-gray-900">
                      ₺{product.price?.toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Takım İçeriği - Sadece takım ürünleri için */}
            {product.is_set && product.set_items && product.set_items.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  🛋️ Takım İçeriği
                </h3>
                <div className="space-y-3">
                  {product.set_items.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg p-3 flex items-center justify-between border border-gray-200">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-600">₺{item.salePrice?.toLocaleString()} / adet</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => updateSetItemQuantity(item.id, Math.max(0, (setItemQuantities[item.id] !== undefined ? setItemQuantities[item.id] : item.quantity) - 1))}
                          className="p-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                        >
                          <MinusIcon className="h-4 w-4" />
                        </button>
                        <span className="text-lg font-medium px-3 min-w-[3rem] text-center">
                          {setItemQuantities[item.id] !== undefined ? setItemQuantities[item.id] : item.quantity}
                        </span>
                        <button
                          onClick={() => updateSetItemQuantity(item.id, (setItemQuantities[item.id] !== undefined ? setItemQuantities[item.id] : item.quantity) + 1)}
                          className="p-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <strong>💡 İpucu:</strong> Takım elemanlarının adetlerini istediğiniz gibi ayarlayabilirsiniz. 
                    Adet sıfır olan elemanlar sepete eklenmeyecektir.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    <strong>🎁 Takım İndirimi:</strong> Takımın tüm elemanlarından minimum adetlerde alırsanız %{settings.set_discount_rate} indirim kazanırsınız!
                    <br />
                    {product.set_items.map((item, index) => (
                      <span key={item.id}>
                        {item.name}: {item.quantity} adet{index < product.set_items.length - 1 ? ' + ' : ''}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            )}

            {/* Options - Sadece tekli ürünler için */}
            {!product.is_set && (
              <div className="space-y-4">
                {/* Size Selection */}
                {product.sizes && product.sizes.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Boyut
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-2 border rounded-lg text-sm font-medium transition-all ${
                            selectedSize === size
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-gray-300 text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Color Selection */}
                {product.colors && product.colors.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Renk
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {product.colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`px-4 py-2 border rounded-lg text-sm font-medium transition-all ${
                            selectedColor === color
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-gray-300 text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Adet
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <MinusIcon className="h-4 w-4" />
                    </button>
                    <span className="text-lg font-medium px-4">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock || 999, quantity + 1))}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className="flex items-center space-x-2">
              {(product.is_active && product.stock > 0) ? (
                <>
                  <CheckIcon className="h-5 w-5 text-emerald-500" />
                  <span className="text-sm text-emerald-600 font-medium">
                    Stokta ({product.stock} adet)
                  </span>
                </>
              ) : (
                <span className="text-sm text-red-600 font-medium">
                  Stokta yok
                </span>
              )}
            </div>

            {/* Add to Cart */}
            <div className="space-y-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={addToCart}
                disabled={!(product.is_active && product.stock > 0)}
                className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-gray-400 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
              >
                <ShoppingCartIcon className="h-5 w-5" />
                <span>Sepete Ekle</span>
              </motion.button>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button className="w-full border-2 border-slate-300 hover:border-slate-400 text-slate-700 py-4 px-6 rounded-xl font-semibold transition-all duration-300">
                  Hemen Satın Al
                </button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAIVisualization}
                  disabled={!hasAIImages()}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
                  title={!hasAIImages() ? 'Bu ürün için AI görseli mevcut değil' : 'AI ile evinize yerleştirin'}
                >
                  <SparklesIcon className="h-5 w-5" />
                  <span>{!hasAIImages() ? 'AI Görsel Yok' : 'Evinde Gör'}</span>
                </motion.button>
              </div>
            </div>

            {/* Features */}
            <div className="pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 flex items-center space-x-2">
                  <TruckIcon className="h-4 w-4 text-slate-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700">
                    {product.delivery_time || '2-4 iş günü'}
                  </span>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 flex items-center space-x-2">
                  <ShieldCheckIcon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700">
                    {product.warranty_months ? `${product.warranty_months} ay garanti` : 'Garanti'}
                  </span>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 flex items-center space-x-2">
                  <CreditCardIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700">Taksit</span>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 flex items-center space-x-2 border border-purple-100">
                  <SparklesIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-purple-700">AI Görsel</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-slate-900 text-slate-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'description' && (
              <div className="space-y-6">
                <p className="text-gray-700 text-lg leading-relaxed">
                  {product.description || 'Ürün açıklaması henüz eklenmemiş.'}
                </p>
                {product.features && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Öne Çıkan Özellikler</h3>
                    <ul className="space-y-3">
                      {(typeof product.features === 'string' 
                        ? product.features.split(',').map(f => f.trim())
                        : Array.isArray(product.features) 
                        ? product.features 
                        : []
                      ).map((feature, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <CheckIcon className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
               
              </div>
            )}

            {activeTab === 'specifications' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Teknik Özellikler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Dinamik özellikler */}
                  {product.dimensions && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Boyutlar</dt>
                      <dd className="text-lg text-gray-900">{product.dimensions}</dd>
                    </div>
                  )}
                  {product.material && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Malzeme</dt>
                      <dd className="text-lg text-gray-900">{product.material}</dd>
                    </div>
                  )}
                  {product.color && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Renk</dt>
                      <dd className="text-lg text-gray-900">{product.color}</dd>
                    </div>
                  )}
                  {product.weight && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Ağırlık</dt>
                      <dd className="text-lg text-gray-900">{product.weight} kg</dd>
                    </div>
                  )}
                  {product.brand && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Marka</dt>
                      <dd className="text-lg text-gray-900">{product.brand}</dd>
                    </div>
                  )}
                  {product.model && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Model</dt>
                      <dd className="text-lg text-gray-900">{product.model}</dd>
                    </div>
                  )}
                  {product.warranty_months && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Garanti</dt>
                      <dd className="text-lg text-gray-900">{product.warranty_months} ay</dd>
                    </div>
                  )}
                  {product.sku && (
                    <div className="border-b border-gray-200 pb-3">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Ürün Kodu</dt>
                      <dd className="text-lg text-gray-900">{product.sku}</dd>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'ai-visualization' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Ürünü Evinizde Görün
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Yapay zeka teknolojisi ile {product.name} ürününü kendi evinizde nasıl göründüğünü görebilirsiniz. 
                    Sadece bir fotoğraf yükleyin ve AI size ürünü evinizde yerleştirilmiş halini göstersin.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
                  <div className="text-center space-y-6">
                    <div className="flex justify-center">
                      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-full p-4">
                        <SparklesIcon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">
                        AI Görselleştirme Nasıl Çalışır?
                      </h4>
                      <p className="text-gray-600 mb-6">
                        En son yapay zeka teknolojileri kullanarak ürünü evinize sanal olarak yerleştiriyoruz.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                      <div className="space-y-3">
                        <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mx-auto border border-purple-200">
                          <CameraIcon className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-900">1. Fotoğraf Yükle</h5>
                          <p className="text-gray-600">Odanızın fotoğrafını çekin veya yükleyin</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mx-auto border border-purple-200">
                          <SparklesIcon className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-900">2. AI İşlem</h5>
                          <p className="text-gray-600">Yapay zeka ürünü odanıza yerleştirir</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mx-auto border border-purple-200">
                          <CheckIcon className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-900">3. Sonuç</h5>
                          <p className="text-gray-600">Ürünün evinizde nasıl duracağını görün</p>
                        </div>
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleAIVisualization}
                      disabled={!hasAIImages()}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-4 px-8 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg mx-auto"
                      title={!hasAIImages() ? 'Bu ürün için AI görseli mevcut değil' : 'AI ile evinize yerleştirin'}
                    >
                      <SparklesIcon className="h-5 w-5" />
                      <span>{!hasAIImages() ? 'AI Görsel Bulunmuyor' : 'AI Görselleştirmeyi Başlat'}</span>
                    </motion.button>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <SparklesIcon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-amber-900 mb-2">
                        AI Görselleştirme İpuçları
                      </h5>
                      <ul className="space-y-1 text-sm text-amber-800">
                        <li>• Odanızın iyi aydınlatılmış bir fotoğrafını kullanın</li>
                        <li>• Ürünü yerleştirmek istediğiniz alanın net görünmesine dikkat edin</li>
                        <li>• En iyi sonuç için yatay (landscape) fotoğraflar tercih edin</li>
                        <li>• Fotoğraf en az 1080p kalitesinde olmalıdır</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Müşteri Değerlendirmeleri</h3>
                <div className="text-center py-12 text-gray-500">
                  <StarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>Henüz değerlendirme bulunmuyor.</p>
                  <p className="text-sm">Bu ürün için ilk değerlendirmeyi siz yapın.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Benzer Ürünler
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedProducts.map((relatedProduct) => (
                <motion.div
                  key={relatedProduct.id}
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/products/${relatedProduct.id}`)}
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={relatedProduct.images && relatedProduct.images.length > 0 
                        ? relatedProduct.images[0]?.url || relatedProduct.images[0]
                        : relatedProduct.image_url || 'https://via.placeholder.com/400x300?text=Resim+Yok'
                      }
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {relatedProduct.name}
                    </h3>
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="flex items-center">
                        <StarIcon className="h-4 w-4 text-amber-400 fill-current" />
                        <span className="text-sm text-gray-600 ml-1">
                          {relatedProduct.rating?.toFixed(1) || '4.5'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        ({relatedProduct.reviews || 0})
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {relatedProduct.is_set ? (
                        <>
                          <span className="text-lg font-bold text-blue-600">
                            ₺{relatedProduct.set_final_price?.toLocaleString() || relatedProduct.price?.toLocaleString()}
                          </span>
                          {relatedProduct.set_original_price && relatedProduct.set_original_price > relatedProduct.set_final_price && (
                            <span className="text-sm text-gray-400 line-through">
                              ₺{relatedProduct.set_original_price.toLocaleString()}
                            </span>
                          )}
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                            🛋️ Takım
                          </span>
                        </>
                      ) : relatedProduct.salePrice ? (
                        <>
                          <span className="text-lg font-bold text-emerald-600">
                            ₺{relatedProduct.salePrice.toLocaleString()}
                          </span>
                          <span className="text-sm text-gray-400 line-through">
                            ₺{relatedProduct.price?.toLocaleString()}
                          </span>
                        </>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">
                          ₺{relatedProduct.price?.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Toast Notification */}
    {/* Image Modal */}
    <AnimatePresence>
      {showImageModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowImageModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative max-w-5xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            {/* Image navigation */}
            {product.images && product.images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors"
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Main image */}
            <img
              src={product.images && product.images.length > 0 
                ? product.images[currentImageIndex]?.url || product.images[currentImageIndex]
                : product.image_url || 'https://via.placeholder.com/800x600?text=Resim+Yok'
              }
              alt={product.name}
              className="w-full h-auto max-h-[90vh] object-contain"
            />

            {/* Image counter */}
            {product.images && product.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1} / {product.images.length}
              </div>
            )}

            {/* Product info overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-white">
              <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
              <p className="text-white/80 text-sm">{product.category}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    <AnimatePresence>
      {showToast && toastData && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          className="fixed bottom-6 right-6 z-50 max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 relative overflow-hidden">
            {/* Close button */}
            <button
              onClick={() => setShowToast(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            {/* Success icon */}
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckIcon className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">
                Sepete Eklendi!
              </h3>
            </div>

            {/* Product info */}
            <div className="flex items-start space-x-4">
              <img
                src={toastData.image || 'https://via.placeholder.com/80x80?text=Resim+Yok'}
                alt={toastData.productName}
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 truncate">
                  {toastData.productName}
                </h4>
                
                {toastData.type === 'set' ? (
                  <div className="mt-2 space-y-1">
                    {toastData.activeSetItems.map((item, index) => (
                      <p key={index} className="text-xs text-gray-600">
                        • {item.name}: {item.quantity} adet
                      </p>
                    ))}
                    <p className="text-sm font-semibold text-blue-600 mt-2">
                      ₺{toastData.finalPrice.toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      {toastData.quantity} adet
                    </p>
                    {(toastData.options.size || toastData.options.color) && (
                      <p className="text-xs text-gray-500 mt-1">
                        {[toastData.options.size, toastData.options.color].filter(Boolean).join(', ')}
                      </p>
                    )}
                    <p className="text-sm font-semibold text-green-600 mt-1">
                      ₺{(toastData.price * toastData.quantity).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-4 flex space-x-3">
              <button
                onClick={() => setShowToast(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Alışverişe Devam
              </button>
              <button
                onClick={() => {
                  setShowToast(false);
                  navigate('/cart');
                }}
                className="flex-1 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors flex items-center justify-center space-x-2"
              >
                <ShoppingCartIcon className="h-4 w-4" />
                <span>Sepeti Gör</span>
              </button>
            </div>

            {/* Progress bar */}
            <motion.div
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: 4, ease: 'linear' }}
              className="absolute bottom-0 left-0 h-1 bg-green-500"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* AI Visualization Modal */}
    <AIVisualizationModal
      isOpen={showAIModal}
      onClose={() => setShowAIModal(false)}
      product={product}
    />
    </>
  );
};

export default ProductDetailPage; 